/**
 * Hook per la mappa clienti: aggrega per ogni cliente le metriche
 * commerciali necessarie per dimensionare i pin sulla mappa.
 *
 * Per ogni cliente espone:
 *   - totalAmount    = somma valori di TUTTI i preventivi (qualsiasi stato)
 *   - wonAmount      = somma valori dei preventivi `won`
 *   - pipelineAmount = somma valori dei preventivi `draft` + `sent`
 *
 * I valori sono calcolati lato client per evitare di gestire view DB ad-hoc
 * solo per la mappa (il numero di clienti per org non è mai grande).
 */
import { useQuery } from '@tanstack/react-query';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useCurrentOrganization } from '@/contexts/OrganizationContext';
import { toast } from 'sonner';
import {
  geocodeAddress,
  geocodeBatch,
  type GeocodeAddress,
} from '@/utils/geocoding/geocodeAddress';
import type { Customer } from './useCustomers';

/**
 * Stati dei preventivi rappresentati sulla mappa con i 4 colori semantici.
 * - `contracted` (firmato contratto) viene aggregato sotto `won` (verde)
 * - `cancelled` viene escluso (non finisce in mappa)
 */
export type EstimateStatusBucket = 'draft' | 'sent' | 'won' | 'lost';

export interface ByStatusBreakdown {
  draft: { amount: number; count: number };
  sent:  { amount: number; count: number };
  won:   { amount: number; count: number };
  lost:  { amount: number; count: number };
}

export interface CustomerMapPoint extends Customer {
  totalAmount: number;
  wonAmount: number;
  pipelineAmount: number;
  estimatesCount: number;
  byStatus: ByStatusBreakdown;
}

function num(v: unknown): number {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

export function useCustomersMap() {
  const { currentOrganizationId } = useCurrentOrganization();

  return useQuery<CustomerMapPoint[]>({
    queryKey: ['customers-map', currentOrganizationId],
    enabled: !!currentOrganizationId,
    staleTime: 60 * 1000,
    queryFn: async () => {
      if (!currentOrganizationId) return [];

      // 1) Carica tutti i clienti dell'org
      const { data: customers, error: cErr } = await supabase
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .from('customers' as any)
        .select('*')
        .eq('organization_id', currentOrganizationId);
      if (cErr) {
        console.error('[useCustomersMap] customers error:', cErr);
        return [];
      }
      const list = (customers ?? []) as unknown as Customer[];
      if (list.length === 0) return [];

      // 2) Carica i preventivi via projects (estimates non ha customer_id diretto)
      const { data: projects } = await supabase
        .from('projects')
        .select('id, customer_id, estimates ( id, total_amount, status )')
        .eq('organization_id', currentOrganizationId);

      // 3) Aggrega per customer_id con breakdown a 4 bucket
      type Agg = {
        total: number;
        won: number;
        pipeline: number;
        count: number;
        byStatus: ByStatusBreakdown;
      };
      const emptyAgg = (): Agg => ({
        total: 0, won: 0, pipeline: 0, count: 0,
        byStatus: {
          draft: { amount: 0, count: 0 },
          sent:  { amount: 0, count: 0 },
          won:   { amount: 0, count: 0 },
          lost:  { amount: 0, count: 0 },
        },
      });
      const agg = new Map<string, Agg>();
      for (const p of projects ?? []) {
        const cid = (p as { customer_id?: string | null }).customer_id;
        if (!cid) continue;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const estimates = ((p as any).estimates ?? []) as Array<{ total_amount?: number | null; status?: string | null }>;
        for (const e of estimates) {
          const status = e.status ?? 'draft';
          // cancelled non finisce in mappa: né nel totale né nei breakdown
          if (status === 'cancelled') continue;
          const amount = num(e.total_amount);
          const cur = agg.get(cid) ?? emptyAgg();
          cur.total += amount;
          cur.count += 1;
          // contracted aggregato sotto won (verde unico)
          let bucket: EstimateStatusBucket | null = null;
          if (status === 'won' || status === 'contracted') bucket = 'won';
          else if (status === 'lost') bucket = 'lost';
          else if (status === 'sent') bucket = 'sent';
          else if (status === 'draft') bucket = 'draft';
          if (bucket) {
            cur.byStatus[bucket].amount += amount;
            cur.byStatus[bucket].count += 1;
            if (bucket === 'won') cur.won += amount;
            else if (bucket === 'draft' || bucket === 'sent') cur.pipeline += amount;
          }
          agg.set(cid, cur);
        }
      }

      return list.map((c): CustomerMapPoint => {
        const a = agg.get(c.id) ?? emptyAgg();
        return {
          ...c,
          totalAmount: a.total,
          wonAmount: a.won,
          pipelineAmount: a.pipeline,
          estimatesCount: a.count,
          byStatus: a.byStatus,
        };
      });
    },
  });
}

/**
 * Geocodifica i clienti senza lat/lng. Best-effort: ignora i clienti senza
 * indirizzo minimo. Rispetta il rate limit di Nominatim.
 */
export function useGeocodePendingCustomers() {
  const { currentOrganizationId } = useCurrentOrganization();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (onProgress?: (done: number, total: number) => void) => {
      if (!currentOrganizationId) return { ok: 0, total: 0 };

      const { data, error } = await supabase
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .from('customers' as any)
        .select('id, address_line, city, zip_code, province, country_code')
        .eq('organization_id', currentOrganizationId)
        .is('latitude', null);

      if (error) throw error;

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const pending = (data ?? []) as unknown as Array<{ id: string } & GeocodeAddress & { country_code?: string | null }>;
      const candidates = pending.filter(
        (c) => (c.addressLine || c.city || c.zipCode || c.province) ||
               (c.country_code), // almeno qualcosa di geocodabile
      );

      // Mappa shape DB → GeocodeAddress
      const inputs: GeocodeAddress[] = pending.map((c) => ({
        addressLine: (c as { address_line?: string | null }).address_line ?? null,
        city: (c as { city?: string | null }).city ?? null,
        zipCode: (c as { zip_code?: string | null }).zip_code ?? null,
        province: (c as { province?: string | null }).province ?? null,
        countryCode: (c as { country_code?: string | null }).country_code ?? 'IT',
      }));

      if (inputs.length === 0) return { ok: 0, total: 0 };

      const results = await geocodeBatch(inputs, onProgress);

      let ok = 0;
      for (let i = 0; i < pending.length; i++) {
        const r = results[i];
        if (!r) continue;
        const { error: upErr } = await supabase
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          .from('customers' as any)
          .update({
            latitude: r.latitude,
            longitude: r.longitude,
            geocoded_at: new Date().toISOString(),
          })
          .eq('id', pending[i].id);
        if (!upErr) ok += 1;
      }

      return { ok, total: pending.length };
    },
    onSuccess: ({ ok, total }) => {
      queryClient.invalidateQueries({ queryKey: ['customers-map'] });
      queryClient.invalidateQueries({ queryKey: ['customers'] });
      if (total === 0) {
        toast.info('Nessun cliente da geocodificare');
      } else {
        toast.success(`Geocodificati ${ok}/${total} clienti`);
      }
    },
    onError: (err) => {
      console.error('[geocode] error:', err);
      toast.error('Errore durante la geocodifica');
    },
  });
}

/**
 * Geocodifica al volo un singolo cliente (es. dopo create/edit) e salva.
 * Da chiamare opportunisticamente: non blocca l'UX se fallisce.
 */
export async function geocodeAndSaveCustomer(customerId: string, addr: GeocodeAddress): Promise<boolean> {
  const r = await geocodeAddress(addr);
  if (!r) return false;
  const { error } = await supabase
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    .from('customers' as any)
    .update({
      latitude: r.latitude,
      longitude: r.longitude,
      geocoded_at: new Date().toISOString(),
    })
    .eq('id', customerId);
  return !error;
}
